import { fromBaseClone as cloneFromBase, fromBaseRecord, fromBaseUuid, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import { readItemField as readRawItemField } from './item-field-reader';
const readItemField = readRawItemField;
import { readLayerListEntryUuid, readInventoryField } from './inventory-raw-readers';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { plainTextDecode } from './possessor-compile';
import { isPhpNumeric } from './php-decimal-values';
import staticData from './from-base-static-registries.json';
import { internFromBaseRandomTables } from './from-base-catalog-random-tables';
import { readInventoryPossessorType } from './inventory-environment';
import { ftInventoryTemplateComputedDefaults } from '../port/k1-compile/functions';
import { json_encode } from '../php-compat';
import { isSelfStatusOrigin } from '../statuses/status-origin';

// This normalization pass rewrites every node it copies or adds to the tree.
const fromBaseClone = <T>(value: T): T => cloneFromBase(value, undefined, true);

export const FROM_BASE_STATIC_REGISTRIES = staticData.registries as unknown as Readonly<Record<string, Readonly<Record<string, Row>>>>;
const stripTypes = new Set(staticData.policies.FT_STRIP_TO_UUID_TYPES);
const forceIntern = new Set(staticData.policies.FT_FORCE_INTERN_STRIP_TYPES);
const denyTypes = new Set(staticData.policies.FT_CATALOGIZE_DENY_TYPES);
const names: Readonly<Record<string, string>> = staticData.names;
const own = (row: object, key: string) => Object.prototype.hasOwnProperty.call(row, key);
const collection = (value: unknown): value is Row | unknown[] => value !== null && typeof value === 'object';
const nonempty = (value: unknown) => collection(value) ? Object.keys(value).length !== 0 : Boolean(value);
const field = (row: Row | unknown[], key: string) => readItemField({ [key]: [(row as Row)[key] ?? null] }, key);
const identity = (row: Row) => typeof row.items === 'object' && row.items !== null ? readItemField({ items: row.items }, 'item_id') : null;
const catalogEntry = (catalog: Row, id: string): Row | null => {
    const row = catalog[id] ?? catalog[id.toUpperCase()] ?? catalog[id.toLowerCase()];
    return fromBaseRecord(row) ? row : null;
};

export interface FromBaseCatalogContext {
    itemField?: typeof readRawItemField;
    definition: (id: string) => Row | null;
    rawDefinition: (id: string) => Row | null;
    abilityTypes: readonly string[];
    randomTableRows?: Readonly<Record<string, readonly Row[] | null>>;
    /** Entries the page already holds (instance templates, the "0" name bucket). A subtree compile
     * seeds its store with them, so its instances thin against the SAME templates the enclosing
     * compile uses, and first-definition precedence keeps the held shape.
     * Copied before use; the held objects are never mutated. */
    heldCatalog?: Readonly<Row>;
}

/** Lossless first-definition precedence, with independently sparse computed buckets. */
export function mergeFromBaseCatalogEntry(catalog: Row, id: string, source: unknown): void {
    if (!fromBaseRecord(source)) { if (!own(catalog, id)) catalog[id] = source; return; }
    const incoming = fromBaseClone(source);
    if (fromBaseRecord(incoming.items)) { delete incoming.item_name; delete incoming.item_type; }
    else for (const key of ['item_name', 'item_type']) if (Array.isArray(incoming[key])) incoming[key] = field(incoming, key) ?? '';
    const current = catalogEntry(catalog, id);
    if (!current) { catalog[id] = incoming; return; }
    const full = nonempty(incoming.item_layer_types), currentFull = nonempty(current.item_layer_types);
    if (full && !currentFull) {
        for (const [key, value] of Object.entries(current)) if (!own(incoming, key)) incoming[key] = value;
        catalog[id] = incoming;
        return;
    }
    if (!full || !currentFull) {
        const target = fromBaseRecord(current.items) ? current.items : current;
        for (const key of ['item_name', 'item_type']) {
            const value = readItemField(incoming, key);
            if (readItemField(target, key) === '' || readItemField(target, key) === null)
                if (typeof value === 'string' && value !== '') target[key] = value;
        }
        return;
    }
    for (const [key, value] of Object.entries(incoming)) {
        if (['item_id', 'item_name', 'item_type'].includes(key)) continue;
        if (!own(current, key)) current[key] = value;
        else if (key === 'computed' && collection(value) && collection(current[key])) {
            const target = current[key] as Row;
            for (const [child, childValue] of Object.entries(value)) {
                if (!own(target, child)) target[child] = childValue;
                else if (collection(childValue) && collection(target[child]))
                    for (const [leaf, leafValue] of Object.entries(childValue)) if (!own(target[child] as Row, leaf)) (target[child] as Row)[leaf] = leafValue;
            }
        }
    }
}

/** ft_intern_shell_catalog's context-free definition/store window. The same
 * module serves definitions and encounter shells; raw catalogs never ride in output. */
export function normalizeFromBaseCatalog(source: Row, context: FromBaseCatalogContext): Row {
    const readItemField = context.itemField ?? readRawItemField;
    const root = fromBaseClone(source), catalog: Row = context.heldCatalog ? fromBaseClone(context.heldCatalog) : {}, riding: Row = {};
    const walk = (node: Row | unknown[], visit: (row: Row | unknown[]) => void): void => {
        for (const value of Object.values(node)) if (collection(value)) walk(value, visit);
        visit(node);
    };
    walk(root, node => {
        const held = field(node, 'catalog');
        if (!collection(held)) return;
        for (const [id, entry] of Object.entries(held)) mergeFromBaseCatalogEntry(riding, id, entry);
        delete (node as Row).catalog;
    });
    function store(id: string, entry: Row, keepPl: boolean): void {
        const held = catalogEntry(catalog, id), computed = fromBaseRecord(entry.computed) ? entry.computed : {};
        const pl = readItemField(computed, 'pl');
        if (held && nonempty(held.item_layer_types)) {
            if (keepPl && held.computed == null && isPhpNumeric(pl) && Number(pl) !== 0) held.computed = { pl };
            return;
        }
        catalog[id] = { item_layer_types: true };
        const { catalog: _catalog, ...ownEntry } = entry;
        const def = fromBaseClone(ownEntry);
        delete def.computed; delete def.item_name; delete def.item_type;
        if (keepPl && isPhpNumeric(pl) && Number(pl) !== 0) def.computed = { pl };
        catalogize(def, true);
        catalog[id] = def;
    }
    function eligible(value: unknown): value is Row {
        if (!fromBaseRecord(value) || typeof identity(value) !== 'string' || !identity(value) || !nonempty(value.item_layer_types)) return false;
        const type = readItemField({ items: value.items }, 'item_type');
        if (typeof type !== 'string' || !type || denyTypes.has(type) || readInventoryField(value, 'inventory_id')) return false;
        return !['encounter_creatures', 'player_characters', 'statuses', 'position', 'pos_arr'].some(key => collection(value[key]));
    }
    function transform(node: Row | unknown[], candidate: (value: unknown) => boolean, replace: (row: Row) => unknown): void {
        for (const [key, value] of Object.entries(node)) {
            if (!collection(value)) continue;
            const excluded = key === 'status_arr';
            if (own(value, '0')) {
                for (const [index, entry] of Object.entries(value)) {
                    if (!excluded && candidate(entry)) (value as Row)[index] = replace(entry as Row);
                    else if (collection(entry)) transform(entry, candidate, replace);
                }
            } else if (!excluded && candidate(value)) (node as Row)[key] = replace(value as Row);
            else transform(value, candidate, replace);
        }
    }
    function catalogize(node: Row | unknown[], keepPl: boolean): void {
        transform(node, eligible, entry => { const id = identity(entry) as string; store(id, entry, keepPl); return id; });
    }
    transform(root, value => {
        if (!fromBaseRecord(value) || !fromBaseRecord(value.items) || nonempty(value.item_layer_types)) return false;
        if (nonempty(value.computed) && (!fromBaseRecord(value.computed) || Object.keys(value.computed).some(key => key !== 'pl'))) return false;
        return Object.keys(value).every(key => ['items', 'item_layer_types', 'computed'].includes(key))
            && fromBaseUuid(identity(value)) && stripTypes.has(String(readItemField(value, 'item_type') ?? ''));
    }, shell => {
        const id = identity(shell) as string, type = String(readItemField(shell, 'item_type') ?? '');
        if (forceIntern.has(type) || !FROM_BASE_STATIC_REGISTRIES[type]?.[id]) {
            const description = type === 'status_effect' ? readItemField(shell, 'item_description') : null;
            if (!own(catalog, id)) catalog[id] = { item_name: readItemField(shell, 'item_name') ?? '', item_type: type,
                ...(typeof description === 'string' && description ? { item_description: description } : {}) };
            else if (description && fromBaseRecord(catalog[id]) && own(catalog[id], 'item_name') && !catalog[id].item_description)
                catalog[id].item_description = description;
        }
        return id;
    });
    // Status definitions compare their canonical shape before the blind hoist.
    const canonical = (value: unknown): unknown => Array.isArray(value) ? value.map(canonical)
        : fromBaseRecord(value) ? Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])])) : value;
    walk(root, node => {
        const status = field(node, 'status_arr');
        if (fromBaseRecord(status) && fromBaseUuid(identity(status))) {
            const id = identity(status) as string, existing = catalogEntry(catalog, id);
            let collapse = false;
            if (!nonempty(status.item_layer_types) && !nonempty(status.computed)) {
                if (!existing) catalog[id] = { item_name: readItemField(status, 'item_name') ?? '',
                    item_type: readItemField(status, 'item_type') ?? '',
                    ...(readItemField(status, 'item_description') ? { item_description: readItemField(status, 'item_description') } : {}) };
                collapse = true;
            } else if (!existing || !fromBaseRecord(existing.items) || !own(existing, 'item_layer_types') && !own(existing, 'computed')) {
                store(id, status, false); collapse = true;
            } else {
                const shaped = { ...status };
                delete shaped.computed; delete shaped.item_name; delete shaped.item_type;
                catalogize(shaped, true);
                for (const [key, value] of Object.entries(shaped)) if (!own(existing, key)) existing[key] = value;
                collapse = JSON.stringify(canonical(shaped)) === JSON.stringify(canonical(existing));
            }
            if (collapse) { (node as Row).status_item_id = id; delete (node as Row).status_arr; }
        }
        const origin = field(node, 'status_origin_arr'), originId = readItemField(node, 'status_origin_id');
        const nestedId = fromBaseRecord(origin) ? identity(origin) : origin;
        if (typeof originId === 'string' && originId && typeof nestedId === 'string'
            && nestedId.toUpperCase() === originId.toUpperCase()) delete (node as Row).status_origin_arr;
    });
    catalogize(root, true);
    const inventoryNodes: Row[] = [];
    walk(root, node => { if (fromBaseRecord(node) && readInventoryField(node, 'inventory_id')) inventoryNodes.push(node); });
    const volatile = new Set(['current_item_charges']);
    const templateFields = ['item_created_by_id', 'item_description', 'homebrew'];
    const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
    const isEnchanted = (node: Row) => phpJsonTruthy(readInventoryField(node, 'enchantment_template_id'))
        || phpJsonTruthy(readItemField({ enchantment_template_id: node.enchantment_template_id }, 'enchantment_template_id'))
        || fromBaseRecord(node.computed) && node.computed.enchantment != null;
    const isResized = (node: Row) => fromBaseUuid(readInventoryField(node, 'inventory_size'));
    const page = (value: unknown): Row => {
        const text = json_encode(value);
        if (text === false) throw new TypeError('Non-JSON template defaults');
        return JSON.parse(text) as Row;
    };
    const templateComputedDefaults = (node: Row, id: string): Row => page(ftInventoryTemplateComputedDefaults(node, id, {
        deps: { possessor_type_of: value => readInventoryPossessorType(page(value)), _ft_resolve_carrier_ref: context.definition },
    } as Parameters<typeof ftInventoryTemplateComputedDefaults>[2]));
    const templateDefinition = (source: Row, id: string, previous: Row | null): Row | null => {
        const sourceItems = { items: source.items };
        const items: Row = { item_id: id, item_name: readItemField(sourceItems, 'item_name') ?? readItemField(previous, 'item_name') ?? '',
            item_type: readItemField(sourceItems, 'item_type') ?? readItemField(previous, 'item_type') ?? '' };
        for (const key of [...templateFields, 'flavor_text']) {
            const own = readItemField(sourceItems, key), value = own != null && own !== '' ? own : readItemField(previous, key);
            if (value != null && value !== '' && !collection(value)) items[key] = value;
        }
        const layers = fromBaseRecord(source.item_layer_types) ? Object.fromEntries(Object.entries(source.item_layer_types).filter(([key]) => !volatile.has(key))) : {};
        const computed = templateComputedDefaults(source, id);
        if (!Object.keys(layers).length && !nonempty(source.assets) && !Object.keys(computed).length) return null;
        return { items, item_layer_types: layers, ...(nonempty(source.assets) ? { assets: fromBaseClone(source.assets) } : {}),
            ...(Object.keys(computed).length ? { computed } : {}),
            ...(fromBaseRecord(source.pictures) && nonempty(source.pictures.item) ? { pictures: { item: fromBaseClone(source.pictures.item) } } : {}) };
    };
    // Complete the immutable template store before any instance loses fields.
    const deferredTemplates = new Map<string, Row>();
    for (const node of inventoryNodes) {
        const id = identity(node) ?? readInventoryField(node, 'inventory_item_id');
        if (!fromBaseUuid(id)) continue;
        const enchanted = isEnchanted(node);
        let def = catalogEntry(catalog, id);
        if (!def || !nonempty(def.item_layer_types)) {
            // Floor layers include local weather/Wet, never authored template defaults.
            if (enchanted || isResized(node) || readInventoryPossessorType(node) === 'encounter') {
                deferredTemplates.set(id, node);
                continue;
            } else if (fromBaseRecord(node.items) && readItemField(node, 'item_name') && readItemField(node, 'item_type')) {
                const template = templateDefinition(node, id, def);
                if (template) { def = template; catalog[id] = template; }
            }
        }
        if (!def) {
            def = { item_name: readItemField(node, 'item_name') ?? '', item_type: readItemField(node, 'item_type') ?? '' };
            catalog[id] = def;
        }
        if (fromBaseRecord(def.items)) for (const key of templateFields) {
            const value = readItemField({ items: node.items }, key); if (value != null && value !== '' && !own(def.items, key)) def.items[key] = value;
        }
        const gallery = fromBaseRecord(node.pictures) && Array.isArray(node.pictures.item) ? node.pictures.item : null;
        if (gallery?.length && !own(def, 'pictures')) def.pictures = { item: fromBaseClone(gallery) };
        if (nonempty(node.assets) && !own(def, 'assets')) def.assets = fromBaseClone(node.assets);
        const defaults = templateComputedDefaults(node, id);
        if (Object.keys(defaults).length) {
            const computed = collection(field(def, 'computed')) ? field(def, 'computed') as Row : {};
            for (const [key, value] of Object.entries(defaults)) if (!own(computed, key)) computed[key] = value;
            def.computed = computed;
        }
    }
    // Resolve mutated and floor templates from the authored definition after the instance pass.
    for (const [id, node] of deferredTemplates) {
        const previous = catalogEntry(catalog, id);
        if (previous && nonempty(previous.item_layer_types)) continue;
        const full = context.definition(id);
        const template = full ? templateDefinition(full, id, previous) : null;
        if (template && nonempty(template.item_layer_types)) store(id, template, false);
        else if (!previous) catalog[id] = { item_name: readItemField(node, 'item_name') ?? '', item_type: readItemField(node, 'item_type') ?? '' };
    }
    for (const node of inventoryNodes) {
        const id = identity(node) ?? readInventoryField(node, 'inventory_item_id'); if (!fromBaseUuid(id)) continue;
        const def = catalogEntry(catalog, id); if (!def) continue;
        if (fromBaseRecord(node.items)) for (const key of ['item_name', 'item_type', 'flavor_text']) delete node.items[key];
        const layers = fromBaseRecord(node.item_layer_types) ? node.item_layer_types : null;
        const defaults = fromBaseRecord(def.item_layer_types) ? def.item_layer_types : null;
        const enchanted = isEnchanted(node);
        if (layers && defaults) {
            if (enchanted) for (const [key, value] of Object.entries(layers)) {
                if (!volatile.has(key) && own(defaults, key) && same(value, defaults[key])) delete layers[key];
            } else {
                const stable = Object.fromEntries(Object.entries(layers).filter(([key]) => !volatile.has(key)));
                if (same(stable, defaults)) for (const key of Object.keys(stable)) delete layers[key];
            }
            if (!Object.keys(layers).length) delete node.item_layer_types;
        }
        if (collection(def.assets) && same(node.assets, def.assets)) delete node.assets;
        if (fromBaseRecord(def.computed) && fromBaseRecord(node.computed) && own(def.computed, 'pl') && own(node.computed, 'pl') && same(def.computed.pl, node.computed.pl)) delete node.computed.pl;
        if (readInventoryPossessorType(node) === 'encounter') {
            const computed = field(node, 'computed'), defaults = field(def, 'computed');
            if (fromBaseRecord(computed) && fromBaseRecord(defaults)) {
                for (const [bucket, key] of [['cost', 'total_gp'], ['weight', 'total']] as const) {
                    const ownValues = field(computed, bucket), baseValues = field(defaults, bucket);
                    if (fromBaseRecord(ownValues) && fromBaseRecord(baseValues) && own(ownValues, key) && own(baseValues, key)
                        && same(field(ownValues, key), field(baseValues, key))) {
                        delete ownValues[key];
                        if (Object.keys(ownValues).length) computed[bucket] = ownValues; else delete computed[bucket];
                    }
                }
                const vitality = field(computed, 'vitality'), baseVitality = field(defaults, 'vitality');
                if (fromBaseRecord(vitality) && fromBaseRecord(baseVitality)
                    && Object.keys(vitality).every(key => ['base_v_current', 'base_v_max', 'base_v_temp', 'base_v_max_temp'].includes(key))) {
                    const current = field(vitality, 'base_v_current'), maximum = field(vitality, 'base_v_max'), overrides: Row = {};
                    if (!same(maximum, field(baseVitality, 'base_v_max'))) overrides.base_v_max = maximum;
                    if (isPhpNumeric(current) && isPhpNumeric(maximum) && Number(current) !== Number(maximum)) overrides.base_v_current = current;
                    for (const key of ['base_v_temp', 'base_v_max_temp']) {
                        const value = field(vitality, key) ?? 0;
                        if (!same(value, field(baseVitality, key) ?? 0)) overrides[key] = value;
                    }
                    if (Object.keys(overrides).length) computed.vitality = overrides; else delete computed.vitality;
                }
                if (Object.keys(computed).length) node.computed = computed; else delete node.computed;
            }
        }
        const template = readInventoryField(node, 'inventory_item_id');
        if (typeof template === 'string' && template.toUpperCase() === id.toUpperCase() && fromBaseRecord(node.items)
            && Object.keys(node.items).every(key => ['item_id', ...templateFields].includes(key))
            && templateFields.every(key => !own(node.items as Row, key) || readItemField(def, key) != null
                && phpJsonString(readItemField(node, key)) === phpJsonString(readItemField(def, key)))) delete node.items;
        if (fromBaseRecord(node.pictures) && fromBaseRecord(def.pictures) && Array.isArray(def.pictures.item) && Array.isArray(node.pictures.item)) {
            const set = new Set(def.pictures.item.map(row => JSON.stringify(row)));
            if (same(node.pictures.item, def.pictures.item) || enchanted && node.pictures.item.every(row => set.has(JSON.stringify(row)))) delete node.pictures.item;
            if (!Object.keys(node.pictures).length) delete node.pictures;
        }
        if (fromBaseRecord(node.pictures)) {
            const pools = [fromBaseRecord(def.pictures) ? def.pictures.item : null, node.pictures.inventory_picture, node.pictures.item];
            const ids = new Set(pools.flatMap(rows => Array.isArray(rows) ? rows : []).map(row => readItemField(row, 'picture_id')).filter(Boolean));
            for (const key of ['inventory_presentation_picture', 'inventory_thumbnail_picture']) {
                const rows = node.pictures[key]; if (!Array.isArray(rows) || !rows.length) continue;
                const additions: Row = {}; let complete = true;
                for (const row of rows) {
                    const id = readItemField(row, 'picture_id'), url = readItemField(row, 'picture_url');
                    if (typeof id !== 'string' || id === '' || !ids.has(id) && (typeof url !== 'string' || url === '')) { complete = false; break; }
                    if (!ids.has(id)) additions[id] = { picture_url: url };
                }
                if (complete) { for (const [id, row] of Object.entries(additions)) if (!own(catalog, id)) catalog[id] = row; delete node.pictures[key]; }
            }
            if (!Object.keys(node.pictures).length) delete node.pictures;
        }
    }
    walk(root, node => {
        if (!own(node, 'requires_level')) return;
        const ref = field(node, 'item_arr') ?? field(node, 'item_id');
        const def = fromBaseRecord(ref) ? ref : typeof ref === 'string' ? catalogEntry(catalog, ref) : null;
        if (!def && typeof ref !== 'string') return;
        const level = field(node, 'requires_level'), layers = def && collection(def.item_layer_types) ? def.item_layer_types : {};
        if (level == null || ['', '0', '1'].includes(phpJsonString(level))
            || field(layers, 'requires_level') !== null && phpJsonString(field(layers, 'requires_level')) === phpJsonString(level)) delete (node as Row).requires_level;
    });
    for (const [id, entry] of Object.entries(riding)) {
        if (fromBaseRecord(entry) && entry.item_layer_types != null) catalogize(entry, true);
        mergeFromBaseCatalogEntry(catalog, id, entry);
    }
    walk(root, node => {
        if (!fromBaseRecord(node) || nonempty(node.item_layer_types) || nonempty(node.computed)) return;
        const id = identity(node);
        if (!fromBaseUuid(id)) return;
        const type = readItemField({ items: node.items }, 'item_type'), name = readItemField({ items: node.items }, 'item_name');
        if (fromBaseUuid(id) && name != null && type != null && !own(catalog, id) && !FROM_BASE_STATIC_REGISTRIES[String(type)]?.[id])
            catalog[id] = { item_name: name, item_type: type };
    });
    const creatureRefs: Readonly<Record<string, string>> = {
        transform_into_creature_on_self_zero_vitality: 'creature', transform_killed_target_into_controlled_creature: 'creature',
        mount_creature: 'creature', monster_bit: 'monster_bit', creature_type_monster_bit: 'monster_bit', creature_sub_type_monster_bit: 'monster_bit',
    };
    const collectRefs = (value: unknown): string[] => {
        if (fromBaseUuid(value)) return [value];
        if (!collection(value)) return [];
        if (own(value, 'item_layer_effect')) return collectRefs(field(value, 'item_layer_effect'));
        return Object.values(value).flatMap(collectRefs);
    };
    const internIdentity = (id: string, fallback: string, description = false): void => {
        if (own(catalog, id)) return;
        const raw = context.rawDefinition(id), name = names[id] ?? plainTextDecode(phpJsonString(readItemField(raw, 'item_name') ?? ''));
        const type = readItemField(raw, 'item_type') ?? fallback;
        if (!name || name === 'Na' || !type) return;
        const text = description ? readItemField(raw, 'item_description') : null;
        catalog[id] = { item_name: name, item_type: type, ...(text ? { item_description: text } : {}) };
    };
    function internRefs(node: Row | unknown[], namedOnly = false, naturalWeapons = true): void {
        for (const [key, value] of Object.entries(node)) {
            const entries = collection(value) ? Object.values(value) : [value];
            if (!namedOnly && ['charged_spell', 'charged_ability', 'passive_ability'].includes(key) && collection(value)) {
                const refs = fromBaseRecord(value) && !own(value, '0') ? [value] : entries;
                for (const entry of refs) {
                    const id = readLayerListEntryUuid(entry);
                    if (!fromBaseUuid(id) || nonempty(catalogEntry(catalog, id)?.item_layer_types)) continue;
                    const def = context.definition(id);
                    if (def) store(id, def, true);
                }
            }
            if (!namedOnly) {
                if (naturalWeapons && key === 'natural_weapon' || ['summon', 'shapeshift'].includes(key) || /^user_choice_\d+_(shapeshift|summon)$/.test(key))
                    for (const entry of entries) if (fromBaseUuid(entry)) internIdentity(entry, key === 'natural_weapon' ? 'natural_weapon' : 'creature');
                if (creatureRefs[key]) for (const id of collectRefs(value)) internIdentity(id, creatureRefs[key]!);
                if (['inventory_equipped', 'inventory_unequipped'].includes(key)) for (const entry of Array.isArray(value) ? value : [value]) {
                    const id = fromBaseUuid(entry) ? entry : fromBaseRecord(entry) && own(entry, 'item_layer_effect')
                        ? readItemField(entry, 'item_layer_effect') : fromBaseRecord(entry) && !readItemField(entry, 'item_name') ? identity(entry) : null;
                    if (fromBaseUuid(id)) internIdentity(id, '');
                }
            } else for (const entry of entries) if (fromBaseUuid(entry) && !own(catalog, entry) && names[entry] && names[entry] !== 'Na') catalog[entry] = { item_name: names[entry] };
            if (collection(value)) internRefs(value, namedOnly, naturalWeapons);
        }
    }
    internRefs(root);
    for (const def of Object.values(catalog)) if (collection(def)) internRefs(def, false, false);
    const creature = fromBaseRecord(root.computed) && fromBaseRecord(root.computed.creature) ? root.computed.creature : {};
    const size = fromBaseRecord(creature.size) && fromBaseRecord(creature.size.breakdown) ? field(creature.size.breakdown, 'final_size_id') : null;
    if (fromBaseUuid(size)) internIdentity(size, 'size', true);
    walk(root, node => {
        const id = field(node, 'status_origin_id');
        if (!fromBaseUuid(id) || isSelfStatusOrigin(field(node, 'status_id'), id) || nonempty(catalogEntry(catalog, id)?.item_layer_types)) return;
        const raw = context.rawDefinition(id), type = readItemField(raw, 'item_type');
        if (typeof type !== 'string' || ![...context.abilityTypes, 'spell', 'curse', 'ability'].includes(type)) return;
        const def = context.definition(id); if (def) store(id, def, true);
    });
    // Origin definitions can introduce summon/picker/loadout references which
    // were not present during the initial charged/passive-definition walk.
    for (const def of Object.values(catalog)) if (collection(def)) internRefs(def, false, false);
    internFromBaseRandomTables(root, catalog, context, store);
    internRefs(root, true);
    for (const def of Object.values(catalog)) if (collection(def)) internRefs(def, true);
    if (Object.keys(catalog).length) root.catalog = Object.fromEntries(Object.keys(catalog).sort().map(id => [id, catalog[id]]));
    const leanAbility = (row: Row): void => {
        const id = field(row, 'item_id'); if (!fromBaseUuid(id)) return;
        const ability = field(row, 'ability_id');
        if (typeof ability === 'string' && ability.toUpperCase() === id.toUpperCase()) delete row.ability_id;
        const def = catalogEntry(catalog, id), name = field(row, 'item_name');
        if (typeof name === 'string' && name && readItemField(def, 'item_name') === name) delete row.item_name;
        const charges = field(row, 'charges'); if (!fromBaseRecord(charges)) return;
        const layers = def && collection(def.item_layer_types) ? def.item_layer_types : {};
        for (const [key, layer] of Object.entries({ max: 'ability_charges', resets_long_rest: 'ability_resets_long_rest', recharge_begin: 'recharge_range_begin', recharge_end: 'recharge_range_end' })) {
            const before = field(charges, key); let value = field(layers, layer);
            if (fromBaseRecord(value)) value = field(value, 'item_layer_effect');
            if (own(charges, key) && isPhpNumeric(before) && isPhpNumeric(value) && Number(before) === Number(value)) delete charges[key];
        }
        row.charges = charges;
    };
    const encounter = fromBaseRecord(root.encounters) ? root.encounters : null;
    const campaign = encounter && field(encounter, 'encounter_master_parent_type') === 'campaign' ? field(encounter, 'encounter_master_parent_id') : null;
    walk(root, node => {
        for (const [key, value] of Object.entries(node)) {
            if (context.abilityTypes.includes(key) && Array.isArray(value)) for (const row of value) if (fromBaseRecord(row)) leanAbility(row);
            if (key === 'enchantment_template_enchantments' && Array.isArray(value)) for (const row of value) if (fromBaseRecord(row)) {
                const id = readItemField(row, 'enchantment_item_id'), name = readItemField(row, 'item_name');
                if (fromBaseUuid(id) && typeof name === 'string' && name !== '' && readItemField(catalogEntry(catalog, id), 'item_name') === name)
                    delete row.item_name;
            }
            if (key === 'computed' && fromBaseRecord(value) && fromBaseRecord(value.creature) && collection(value.creature.abilities))
                for (const list of Object.values(value.creature.abilities)) if (collection(list)) for (const row of Object.values(list)) if (fromBaseRecord(row)) leanAbility(row);
            if (key === 'user_choices' && Array.isArray(value)) for (const row of value) if (fromBaseRecord(row))
                for (const name of ['choice_item_id', 'exact_choice_item_id', 'choice']) {
                    const shell = field(row, `${name}_shell`), ref = field(row, name);
                    if (typeof shell === 'string' && typeof ref === 'string' && ref && shell.toUpperCase() === ref.toUpperCase()) delete row[`${name}_shell`];
                }
            if (key === 'statuses' && collection(value)) for (const side of ['target', 'caster']) if (collection((value as Row)[side]))
                for (const row of Object.values((value as Row)[side] as Row)) if (fromBaseRecord(row) && field(row, 'status_id') != null) {
                    const caster = field(row, 'status_caster_id'), target = field(row, 'status_target_id'), ownCampaign = field(row, 'status_campaign_id');
                    if (typeof caster === 'string' && caster && typeof target === 'string' && target && caster.toUpperCase() === target.toUpperCase()) delete row.status_caster_id;
                    if (typeof campaign === 'string' && typeof ownCampaign === 'string' && ownCampaign.toUpperCase() === campaign.toUpperCase()) delete row.status_campaign_id;
                }
        }
        for (const key of ['all_sources', 'inventory_in_container', 'difficult_terrain_rects', 'difficult_terrain_boxes',
            'difficult_terrain_footprints', 'difficult_terrain_inventory_ids', 'difficult_terrain_zone_keys'])
            if (Array.isArray((node as Row)[key]) && !((node as Row)[key] as unknown[]).length) delete (node as Row)[key];
        delete (node as Row).catalog_defaults_v;
        const encounter = field(node, 'encounter_creatures');
        const encounterPictures = fromBaseRecord(encounter) ? field(encounter, 'pictures') : null;
        if (fromBaseRecord(encounter) && fromBaseRecord(encounterPictures)) {
            const player = field(node, 'player_characters'), ownPictures = field(node, 'pictures');
            const playerPictures = fromBaseRecord(player) ? field(player, 'pictures') : null;
            const pools = [fromBaseRecord(playerPictures) ? field(playerPictures, 'player_character_picture') : null,
                fromBaseRecord(ownPictures) ? field(ownPictures, 'item') : null, field(encounterPictures, 'encounter_creature_picture')];
            const pictureId = (row: unknown): string => fromBaseRecord(row)
                ? phpJsonString(readItemField(row, 'picture_id')).toLowerCase() : '';
            const ids = new Set(pools.flatMap(pool => collection(pool) ? Object.values(pool).map(pictureId).filter(Boolean) : []));
            for (const [type, key] of [['encounter_creature_presentation_picture', 'encounter_creature_presentation_pic_id'],
                ['encounter_creature_thumbnail_picture', 'encounter_creature_thumbnail_pic_id']] as const) {
                const rows = field(encounterPictures, type), pointer = phpJsonString(readItemField(encounter, key)).toLowerCase();
                if (collection(rows) && Object.keys(rows).length && ids.has(pointer)
                    && Object.values(rows).every(row => ids.has(pictureId(row)))) delete encounterPictures[type];
            }
            if (Object.keys(encounterPictures).length) encounter.pictures = encounterPictures;
            else delete encounter.pictures;
            (node as Row).encounter_creatures = encounter;
        }
        const derived = field(node, 'computed');
        if (fromBaseRecord(derived) && collection(derived.spellsight_signature) && !Object.keys(derived.spellsight_signature).length) {
            delete derived.spellsight_signature;
            if (Object.keys(derived).length) (node as Row).computed = derived; else delete (node as Row).computed;
        }
        const inventory = (node as Row).inventory;
        if (fromBaseRecord(inventory) && readInventoryField(node, 'inventory_id')) {
            const quantity = readInventoryField(node, 'item_qty');
            if (isPhpNumeric(quantity) && Number(quantity) === 1) delete inventory.item_qty;
            const presentation = readInventoryField(node, 'inventory_presentation_pic_id'), thumbnail = readInventoryField(node, 'inventory_thumbnail_pic_id');
            if (typeof presentation === 'string' && presentation && typeof thumbnail === 'string' && presentation.toUpperCase() === thumbnail.toUpperCase()) delete inventory.inventory_thumbnail_pic_id;
            const template = readInventoryField(node, 'inventory_item_id');
            if (fromBaseUuid(template)) for (const [key, column] of [['inventory_description', 'item_description'], ['inventory_flavor_text', 'flavor_text']]) {
                const text = readInventoryField(node, key!);
                if (typeof text === 'string' && text && readItemField(catalogEntry(catalog, template), column!) === text) delete inventory[key!];
            }
        }
        const arr = field(node, 'item_arr');
        if (fromBaseUuid(arr) && !own(node, 'item_id')) { (node as Row).item_id = arr; delete (node as Row).item_arr; }
        if (fromBaseUuid(field(node, 'nest')) && typeof field(node, 'item_id') === 'string'
            && String(field(node, 'nest')).toUpperCase() === String(field(node, 'item_id')).toUpperCase()) delete (node as Row).nest;
        if (Array.isArray(node) && node.length > 1) {
            const carried = new Set(node.filter(fromBaseRecord).flatMap(row => ['item_id', 'ability_id', 'item_arr'].map(key => {
                const ref = field(row, key); return fromBaseRecord(ref) ? identity(ref) : ref;
            })).filter(fromBaseUuid).map(id => id.toUpperCase()));
            const seen = new Set<string>();
            const keep = node.filter(entry => {
                if (!fromBaseUuid(entry)) return true;
                const id = entry.toUpperCase(); if (carried.has(id) || seen.has(id)) return false;
                seen.add(id); return true;
            });
            node.splice(0, node.length, ...keep);
        }
    });
    return root;
}
